import { useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  QueryStateBoundary,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { centsToUsd } from "@/lib/money";
import { formatShortDate } from "@/features/events/eventDate";
import { BOOKING_STATUS_BADGE_VARIANT, BOOKING_STATUS_LABELS } from "@/api/types/booking";
import {
  PARTY_ADDON_LABELS,
  PARTY_ADDON_UNIT_PRICE_CENTS,
  PARTY_FORMAT_LABELS,
  PARTY_OCCASION_LABELS,
  type PartyAddon,
  type PartyAddonType,
} from "@/api/types/party";
import {
  useAddPartyAddon,
  useCancelParty,
  useCompleteParty,
  useConfirmParty,
  useCreatePartyPortalLink,
  useGeneratePartyInvoice,
  useParty,
} from "@/api/hooks/useParties";

interface PartyDetailDialogProps {
  partyId: string | null;
  onClose: () => void;
}

/** Party detail: add-ons, suggested-upsell chips (occasion-survey-driven —
 * app/services/party_pricing.py::suggest_addons), status transitions, and
 * generate-invoice, mirroring features/bookings/BookingDetailDialog.tsx's
 * shape. */
export function PartyDetailDialog({ partyId, onClose }: PartyDetailDialogProps) {
  const navigate = useNavigate();
  const { data: party, isLoading, error, refetch } = useParty(partyId ?? undefined);

  const confirmParty = useConfirmParty();
  const cancelParty = useCancelParty();
  const completeParty = useCompleteParty();
  const generateInvoice = useGeneratePartyInvoice();
  const addAddon = useAddPartyAddon();
  const createPortalLink = useCreatePartyPortalLink();

  async function handleConfirm() {
    if (!party) return;
    try {
      await confirmParty.mutateAsync(party.id);
      toastSuccess("Party confirmed");
    } catch (err) {
      toastError("Couldn't confirm party", getErrorMessage(err));
    }
  }

  async function handleCancel() {
    if (!party) return;
    try {
      await cancelParty.mutateAsync(party.id);
      toastSuccess("Party cancelled");
    } catch (err) {
      toastError("Couldn't cancel party", getErrorMessage(err));
    }
  }

  async function handleComplete() {
    if (!party) return;
    try {
      await completeParty.mutateAsync(party.id);
      toastSuccess("Party marked completed");
    } catch (err) {
      toastError("Couldn't complete party", getErrorMessage(err));
    }
  }

  async function handleGenerateInvoice() {
    if (!party) return;
    try {
      const invoice = await generateInvoice.mutateAsync(party.id);
      toastSuccess("Invoice generated", invoice.number);
    } catch (err) {
      toastError("Couldn't generate invoice", getErrorMessage(err));
    }
  }

  async function handleAddSuggestedAddon(addonType: PartyAddonType) {
    if (!party) return;
    try {
      await addAddon.mutateAsync({ partyId: party.id, addon_type: addonType, quantity: party.headcount || 1 });
      toastSuccess("Add-on added", PARTY_ADDON_LABELS[addonType]);
    } catch (err) {
      toastError("Couldn't add add-on", getErrorMessage(err));
    }
  }

  async function handleCopyPortalLink() {
    if (!party) return;
    try {
      const url = await createPortalLink.mutateAsync(party.id);
      await navigator.clipboard.writeText(url);
      toastSuccess("Portal link copied");
    } catch (err) {
      toastError("Couldn't create portal link", getErrorMessage(err));
    }
  }

  const remainingSuggestions = party
    ? party.suggested_addons.filter((s) => !party.addons.some((a) => a.addon_type === s))
    : [];

  function addonLabel(addon: PartyAddon): string {
    return addon.catalog_item_name ?? PARTY_ADDON_LABELS[addon.addon_type];
  }

  return (
    <Dialog open={!!partyId} onClose={onClose} ariaLabel="Party detail">
      <DialogContent size="md">
        <DialogHeader onClose={onClose}>Party detail</DialogHeader>
        <DialogBody className="space-y-4">
          <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
            {party && (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-display text-lg font-semibold text-text-primary">{party.resource_name}</p>
                    <p className="text-sm text-text-secondary">
                      {formatShortDate(party.booking_date)}
                      {party.slot_label ? ` · ${party.slot_label}` : ""}
                      {party.extra_hour ? " (+1 hr)" : ""}
                    </p>
                  </div>
                  <Badge variant={BOOKING_STATUS_BADGE_VARIANT[party.status]}>
                    {BOOKING_STATUS_LABELS[party.status]}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm text-text-secondary">
                  <p>Format: {PARTY_FORMAT_LABELS[party.format]}</p>
                  <p>Occasion: {PARTY_OCCASION_LABELS[party.occasion]}</p>
                  <p>Headcount: {party.headcount ?? "—"}</p>
                  <p>Child's age: {party.child_age ?? "—"}</p>
                  <p>Base price: {centsToUsd(party.base_price_cents)}</p>
                </div>

                <div>
                  <p className="mb-1 text-sm font-semibold text-text-primary">Add-ons &amp; selections</p>
                  {party.addons.length === 0 ? (
                    <p className="text-sm text-text-muted">No add-ons yet.</p>
                  ) : (
                    <ul className="space-y-1 text-sm text-text-secondary" data-testid="party-addons-list">
                      {party.addons.map((addon) => (
                        <li key={addon.id} className="flex items-center justify-between">
                          <span>
                            {addonLabel(addon)} x{addon.quantity}
                            {addon.source === "portal" && (
                              <Badge variant="info" size="sm" className="ml-2">
                                Requested via portal
                              </Badge>
                            )}
                          </span>
                          <span>{centsToUsd(addon.total_cents)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {remainingSuggestions.length > 0 && (
                  <div>
                    <p className="mb-1 text-sm font-semibold text-text-primary">Suggested add-ons</p>
                    <div className="flex flex-wrap gap-2" data-testid="party-suggested-addons">
                      {remainingSuggestions.map((addonType) => (
                        <button
                          key={addonType}
                          type="button"
                          onClick={() => void handleAddSuggestedAddon(addonType)}
                          disabled={addAddon.isPending}
                          className="inline-flex items-center gap-1 rounded-full border border-brass/40 bg-brass/10 px-3 py-1 text-xs font-medium text-brass hover:bg-brass/20 disabled:opacity-50"
                          data-testid={`suggested-addon-${addonType}`}
                        >
                          + {PARTY_ADDON_LABELS[addonType]} ({centsToUsd(PARTY_ADDON_UNIT_PRICE_CENTS[addonType])}/ea)
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-sm text-text-secondary">
                  Total: <span className="font-semibold text-text-primary" data-testid="party-total-price">{centsToUsd(party.total_price_cents)}</span>
                </p>

                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="secondary" onClick={() => void handleCopyPortalLink()} disabled={createPortalLink.isPending}>
                    Copy portal link
                  </Button>
                  {party.invoice_id ? (
                    <Button size="sm" variant="secondary" onClick={() => navigate(`/money/${party.invoice_id}`)}>
                      View invoice
                    </Button>
                  ) : (
                    (party.status === "confirmed" || party.status === "completed") && (
                      <Button size="sm" onClick={() => void handleGenerateInvoice()} disabled={generateInvoice.isPending} data-testid="generate-party-invoice">
                        {generateInvoice.isPending ? "Generating…" : "Generate invoice"}
                      </Button>
                    )
                  )}
                </div>
              </>
            )}
          </QueryStateBoundary>
        </DialogBody>
        {party && (
          <DialogFooter>
            {(party.status === "inquiry" || party.status === "confirmed") && (
              <Button variant="secondary" onClick={() => void handleCancel()} disabled={cancelParty.isPending}>
                Cancel party
              </Button>
            )}
            {party.status === "inquiry" && (
              <Button onClick={() => void handleConfirm()} disabled={confirmParty.isPending} data-testid="confirm-party">
                {confirmParty.isPending ? "Confirming…" : "Confirm"}
              </Button>
            )}
            {party.status === "confirmed" && (
              <Button onClick={() => void handleComplete()} disabled={completeParty.isPending}>
                {completeParty.isPending ? "Completing…" : "Mark completed"}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
